import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

/**
 * Dev-only one-click switcher between the candidate and admin sign-in screens,
 * so the URL never has to be typed by hand.
 *
 * The button always shows where it will take you: on /admin/sign-in it reads
 * "Candidate", on /sign-in it reads "Admin". One click = one jump.
 *
 * Shown ONLY when both hold:
 *   - the host is in ALLOWED_HOSTS (never preprod / production)
 *   - the current route is one of the two sign-in screens, so it disappears
 *     the moment anyone is logged in
 */
@Component({
  selector: 'app-env-switcher',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './env-switcher.component.html',
  styleUrls: ['./env-switcher.component.scss'],
})
export class EnvSwitcherComponent implements OnInit, OnDestroy {
  /** Hosts where the switcher is allowed to appear. */
  private static readonly ALLOWED_HOSTS = [
    'localhost',
    '127.0.0.1',
    'qa.iqigai.ai',
    'fractalqa.iqigai.ai',
    // 'qalite.iqigai.ai',
  ];

  private static readonly CANDIDATE_PATH = '/sign-in';
  private static readonly ADMIN_PATH = '/admin/sign-in';
  // private static readonly SUPER_ADMIN_PATH = '/super-admin/sign-in';

  private readonly hostAllowed = EnvSwitcherComponent.ALLOWED_HOSTS.includes(
    window.location.hostname
  );

  private routerSub?: Subscription;

  /** Current route without query string / fragment. */
  private path = '';

  constructor(private readonly router: Router) {}

  ngOnInit(): void {
    if (!this.hostAllowed) {
      return;
    }
    this.path = this.stripUrl(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => (this.path = this.stripUrl(event.urlAfterRedirects)));
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  /** Only the two sign-in screens show the button — never once logged in. */
  get enabled(): boolean {
    return (
      this.hostAllowed &&
      (this.path === EnvSwitcherComponent.CANDIDATE_PATH ||
        this.path === EnvSwitcherComponent.ADMIN_PATH)
    );
  }

  /** Where a click sends you — the opposite sign-in screen. */
  get targetPath(): string {
    return this.path === EnvSwitcherComponent.ADMIN_PATH
      ? EnvSwitcherComponent.CANDIDATE_PATH
      : EnvSwitcherComponent.ADMIN_PATH;
  }

  get targetLabel(): string {
    return this.path === EnvSwitcherComponent.ADMIN_PATH ? 'Candidate' : 'Admin';
  }

  switch(): void {
    this.router.navigateByUrl(this.targetPath);
  }

  /** Ctrl/Cmd + Shift + A — same jump without touching the mouse. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.enabled) {
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'a') {
      event.preventDefault();
      this.switch();
    }
  }

  private stripUrl(url: string): string {
    return url.split(/[?#]/)[0];
  }
}
